//internal-deps
import { AllUpiIntentAvailableMethodsResponse, OrderData, UpiIntentAppMeta } from "@swiggy-private/payment-types";
import { VALID_PAYMENT_GROUP_TYPES, VALID_UPI_PAYMENT_CODE } from "@swiggy-private/payment-core";
import { UpiPaymentMethod, PaymentMethodDef } from "@swiggy-private/payment-ui";
import { isEmpty } from "@swiggy-private/js-utils";

//interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

//helpers
import { getAllMethodsOfGroup } from "./PaymentHelper";

//constants
import { POLLING_CONFIG } from "@payment-client/constants/Options";
import { THIRD_PARTY_UPI_INTENT } from "@swiggy-private/payment-ui";

// utils
import { getBridgeSdk } from "@payment-client/utils/BridgeSdk";

export const isPaymentMethodUpiAndPaymentCodeUpiIntent = (method: PaymentMethodDef): method is UpiPaymentMethod => {
    return (
        method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.UPI &&
        method.paymentCode.toUpperCase() === VALID_UPI_PAYMENT_CODE.UPI_INTENT
    );
};

export const isPaymentMethodUpiAndPaymentCodePhonePeUpi = (method: PaymentMethodDef): method is UpiPaymentMethod => {
    return (
        method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.UPI &&
        method.paymentCode.toUpperCase() === VALID_UPI_PAYMENT_CODE.PHONEPE_UPI
    );
};

export const isUpiIntentPaymentMethod = (method: PaymentMethodDef): method is UpiPaymentMethod => {
    return isPaymentMethodUpiAndPaymentCodeUpiIntent(method) || isPaymentMethodUpiAndPaymentCodePhonePeUpi(method);
};

export function getUpiIntentMethods(paymentResponse: GetPaymentMethodsResponse): string[] {
    const upiMethods = getAllMethodsOfGroup(paymentResponse.paymentGroup, VALID_PAYMENT_GROUP_TYPES.UPI);
    const upiIntentMethods = upiMethods.filter(
        (method) =>
            method.paymentCode.toUpperCase() === VALID_UPI_PAYMENT_CODE.UPI_INTENT ||
            method.paymentCode.toUpperCase() === VALID_UPI_PAYMENT_CODE.PHONEPE_UPI,
    );
    const methodNames = upiIntentMethods.map((method) => method.name);

    //Removing duplicates as upi methods might be there in upi group and turbo pay
    return Object.keys(
        methodNames.reduce(
            (acc, methodName) => {
                if (methodName in acc) {
                    return acc;
                }
                acc[methodName] = true;
                return acc;
            },
            {} as { [key: string]: boolean },
        ),
    );
}

export function getWhiteListedUpiIntentApps(paymentResponse: GetPaymentMethodsResponse): string[] {
    const upiMethods = getAllMethodsOfGroup(paymentResponse.paymentGroup, VALID_PAYMENT_GROUP_TYPES.UPI);
    const thirdPartyUpiIntentMethodDetails = upiMethods.find(
        (method) => method.name === THIRD_PARTY_UPI_INTENT,
    ) as UpiPaymentMethod;

    return thirdPartyUpiIntentMethodDetails?.meta.whitelistedApps ?? [];
}

/**
 * BE-driven kill switch for the "show all installed UPI apps" flow. Reads the
 * `showAllInstalledApps` flag from the `third.party` UPI method meta. When true, Portal
 * shows every installed UPI app from the Native Bridge; when false/absent, Portal keeps
 * the existing whitelist-based filtering. All XP/rollout control stays at BE.
 */
export function shouldShowAllInstalledUpiApps(paymentResponse: GetPaymentMethodsResponse): boolean {
    const upiMethods = getAllMethodsOfGroup(paymentResponse.paymentGroup, VALID_PAYMENT_GROUP_TYPES.UPI);
    const thirdPartyUpiIntentMethodDetails = upiMethods.find(
        (method) => method.name === THIRD_PARTY_UPI_INTENT,
    ) as UpiPaymentMethod;

    return thirdPartyUpiIntentMethodDetails?.meta.showAllInstalledApps ?? false;
}

export function getUpiIntentPollingDuration(orderData?: OrderData): number {
    const pollingDurationMs = (Number(orderData?.confirmationTimeMinutes) || 0) * 60 * 1000;
    return Number(pollingDurationMs || POLLING_CONFIG.defaultUpiPollingDuration);
}

export function getUpiCollectPollingDuration(orderData?: OrderData): number {
    const pollingDurationMs = (Number(orderData?.confirmationTimeMinutes) || 0) * 60 * 1000;
    return Number(pollingDurationMs || POLLING_CONFIG.pollingDurationInMS);
}

export const allUpiIntentAvailableMethods = async (
    methods: string[],
): Promise<AllUpiIntentAvailableMethodsResponse> => {
    try {
        const data = await getBridgeSdk().allUpiIntentAvailableMethods({ methods });

        if (!data || isEmpty(data?.upiAppsDetails)) {
            return { upiAppsDetails: [] };
        }
        return data;
    } catch (e) {
        return { upiAppsDetails: [] };
    }
};

export const getUpiPackageNameFromNBResponse = (
    allUpiIntentAvailableMethods: AllUpiIntentAvailableMethodsResponse,
): string[] => {
    return allUpiIntentAvailableMethods?.upiAppsDetails?.map((upiApp) => upiApp?.packageName);
};

export const shouldShowThirdPartyUpiIntentApps = (paymentResponse: GetPaymentMethodsResponse | undefined): boolean => {
    if (isEmpty(paymentResponse)) {
        return false;
    }

    const upiIntentMethods = getUpiIntentMethods(paymentResponse as GetPaymentMethodsResponse);
    return upiIntentMethods?.includes(THIRD_PARTY_UPI_INTENT);
};

export const getThirdPartyUpiIntentMethods = (
    whitelistedIntentApps: string[],
    upiIntentMethodsFromGpo: string[],
    allAvailableUpiIntentMethods: string[],
    showAllInstalledApps: boolean,
): string[] => {
    // New flow (BE flag on): Portal depends purely on the NB response and shows every installed
    // UPI app. Existing flow (flag off): restrict third-party apps to the BE whitelist.
    // In both cases we drop apps already surfaced as regular GPO methods to avoid duplicate tiles,
    // and keep only device-installed apps.
    const candidateApps = showAllInstalledApps ? allAvailableUpiIntentMethods : whitelistedIntentApps;

    return candidateApps
        .filter((method) => !upiIntentMethodsFromGpo.includes(method))
        .filter((method) => allAvailableUpiIntentMethods.includes(method));
};

// filter GPO regular upi intent methods that are installed in the device
export const getGpoAvailableUpiIntentMethods = (
    upiIntentMethodsFromGpo: string[],
    allAvailableUpiIntentMethods: string[],
) => {
    return upiIntentMethodsFromGpo?.filter((upiIntentMethod) => allAvailableUpiIntentMethods.includes(upiIntentMethod));
};

export const getThirdPartyUpiIntentAppsDetails = (
    thirdPartyUpiIntentMethods: string[],
    allInstalledUpiAppsDetails: AllUpiIntentAvailableMethodsResponse,
) => {
    // return third party upi intent app details by taking details from device installed upi app details if exist
    return thirdPartyUpiIntentMethods.reduce<UpiIntentAppMeta[]>((acc, method) => {
        const intentMethodDetails = allInstalledUpiAppsDetails.upiAppsDetails.find(
            (upiMethod) => upiMethod.packageName === method,
        );
        if (!isEmpty(intentMethodDetails)) {
            acc.push(intentMethodDetails);
        }
        return acc;
    }, []);
};

export const processAndGetFinalUpiIntentData = (
    paymentResponse: GetPaymentMethodsResponse,
    allInstalledUpiAppsDetails: AllUpiIntentAvailableMethodsResponse,
): { sortedUpiApps: string[]; thirdPartyUpiIntentAppsDetails: AllUpiIntentAvailableMethodsResponse } => {
    // get all upi intent app codes from NB response
    const allAvailableUpiIntentMethods = getUpiPackageNameFromNBResponse(allInstalledUpiAppsDetails);

    // initializing third party apps details with default value
    const thirdPartyUpiIntentAppsDetails: AllUpiIntentAvailableMethodsResponse = {
        upiAppsDetails: [],
    };

    if (isEmpty(allAvailableUpiIntentMethods) || isEmpty(paymentResponse)) {
        return { sortedUpiApps: [], thirdPartyUpiIntentAppsDetails };
    }

    // get all upi intent methods from GPO response
    const upiIntentMethodsFromGpo = getUpiIntentMethods(paymentResponse);

    // get all whitelisted upi intent apps list from GPO
    const whitelistedIntentApps = getWhiteListedUpiIntentApps(paymentResponse);

    // BE-driven flag deciding new (all installed) vs existing (whitelist) flow
    const showAllInstalledApps = shouldShowAllInstalledUpiApps(paymentResponse);

    // get all displayable third party apps in the same order of whitelisted apps
    const thirdPartyUpiIntentMethods = getThirdPartyUpiIntentMethods(
        whitelistedIntentApps,
        upiIntentMethodsFromGpo,
        allAvailableUpiIntentMethods,
        showAllInstalledApps,
    );

    // get installed GPO regular upi intent methods
    const sortedUpiApps = getGpoAvailableUpiIntentMethods(upiIntentMethodsFromGpo, allAvailableUpiIntentMethods);

    // updating displayable third party app details
    thirdPartyUpiIntentAppsDetails.upiAppsDetails = getThirdPartyUpiIntentAppsDetails(
        thirdPartyUpiIntentMethods,
        allInstalledUpiAppsDetails,
    );

    return { sortedUpiApps: sortedUpiApps.concat(thirdPartyUpiIntentMethods), thirdPartyUpiIntentAppsDetails };
};
